import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { officesApi } from '../lib/api';
import { Plus, Search, UserCheck, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const TellersPage = () => {
  const [tellers, setTellers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const [form, setForm] = useState({ name: '', office_id: '', counter_number: '', description: '', cashier_employee_id: '', start_date: '', end_date: '', status: 'active' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [tRes, oRes] = await Promise.all([api.get('/tellers'), officesApi.list()]);
      setTellers(tRes.data);
      setOffices(oRes.data);
    } catch (e) { setTellers([]); }
    setLoading(false);
  };

  const searchEmployees = async (q) => {
    setEmpSearch(q);
    if (q.length < 2) { setEmpResults([]); return; }
    try { const res = await api.get(`/employees/search?q=${encodeURIComponent(q)}`); setEmpResults(res.data || []); }
    catch { setEmpResults([]); }
  };

  const handleSubmit = async () => {
    if (!form.name) return toast.error('Station name is required');
    try {
      if (editId) { await api.put(`/tellers/${editId}`, form); toast.success('Station updated'); }
      else { await api.post('/tellers', form); toast.success('Teller station created'); }
      resetForm(); load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleEdit = (t) => {
    setEditId(t.id);
    setForm({ name: t.name || '', office_id: t.office_id || '', counter_number: t.counter_number || '', description: t.description || '', cashier_employee_id: t.cashier_employee_id || '', start_date: t.start_date || '', end_date: t.end_date || '', status: t.status || 'active' });
    setShowModal(true);
  };

  const resetForm = () => {
    setShowModal(false); setEditId(null); setEmpSearch(''); setEmpResults([]);
    setForm({ name: '', office_id: '', counter_number: '', description: '', cashier_employee_id: '', start_date: '', end_date: '', status: 'active' });
  };

  return (
    <AdminSubPage title="Teller / Cashier Station Management" breadcrumb={['Admin', 'Organization', 'Teller / Cashier Stations']} backPath="/dashboard/admin/organization" actionLabel="+ Create Station" onAction={() => { resetForm(); setShowModal(true); }}>
      <p className="text-xs text-gray-500 mb-4">Manage physical cash stations at each branch. Assign an employee as the cashier for each station.</p>
      <div className="bg-white border border-gray-200" data-testid="tellers-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Station ID</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Station Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Counter</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Branch</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Assigned Cashier</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            tellers.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">No teller stations configured.</td></tr> :
            tellers.map((t, idx) => (
              <tr key={t.id} className="border-b hover:bg-gray-50" data-testid={`teller-row-${t.id}`}>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs bg-gray-100 text-[#1f1f1f] px-2 py-1" data-testid={`station-id-${t.id}`}>
                    STN-{String(idx + 1).padStart(3, '0')}
                  </span>
                </td>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{t.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{t.counter_number || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{offices.find(o => o.id === t.office_id)?.name || '-'}</td>
                <td className="px-4 py-3">
                  {t.cashier_name ? (
                    <span className="text-sm text-[#1f1f1f] inline-flex items-center gap-1"><UserCheck className="w-3.5 h-3.5 text-emerald-500" />{t.cashier_name}</span>
                  ) : (
                    <span className="text-xs text-gray-400 italic">Unassigned</span>
                  )}
                </td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 ${t.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{t.status}</span></td>
                <td className="px-4 py-3">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(t)} className="text-gray-500 hover:text-[#C5A059]"><Edit2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent className="max-w-lg" aria-describedby="teller-desc">
          <DialogHeader><DialogTitle>{editId ? 'Edit Teller Station' : 'Create Teller Station'}</DialogTitle></DialogHeader>
          <p id="teller-desc" className="sr-only">Teller cash station form</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Station Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Counter 1" data-testid="teller-name" /></div>
              <div><Label>Counter Number</Label><Input value={form.counter_number} onChange={e => setForm({...form, counter_number: e.target.value})} placeholder="e.g., C1, W2" data-testid="teller-counter" /></div>
            </div>
            <div><Label>Branch *</Label>
              <Select value={form.office_id} onValueChange={v => setForm({...form, office_id: v})}>
                <SelectTrigger data-testid="teller-office"><SelectValue placeholder="Select Branch" /></SelectTrigger>
                <SelectContent>{offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign Cashier (Employee)</Label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <Input
                  className="pl-9"
                  value={empSearch}
                  onChange={e => searchEmployees(e.target.value)}
                  placeholder="Search employee by name or ID..."
                  data-testid="cashier-search"
                />
              </div>
              {empResults.length > 0 && (
                <div className="border border-gray-200 mt-1 max-h-32 overflow-y-auto">
                  {empResults.map(emp => (
                    <button key={emp.id} onClick={() => { setForm({...form, cashier_employee_id: emp.id}); setEmpSearch(`${emp.first_name} ${emp.last_name} (${emp.employee_number || 'no ID'})`); setEmpResults([]); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-50">
                      <span className="font-medium">{emp.first_name} {emp.last_name}</span>
                      {emp.employee_number && <span className="text-xs font-mono text-gray-400 ml-2">{emp.employee_number}</span>}
                    </button>
                  ))}
                </div>
              )}
              {form.cashier_employee_id && !empSearch && <p className="text-xs text-emerald-600 mt-1">Cashier assigned (ID: {form.cashier_employee_id.substring(0, 8)}...)</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="e.g., Main branch front desk" /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="teller-submit">{editId ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default TellersPage;
